<template>
  <div class="results_full_width">
    <div v-if="error" class="result_error">{{ error }}</div>
    <div v-if="resultJson" class="result_box">
      <div class="result_header">
        <div class="result_header_title">
          <h3>Rezultati iskanja</h3>
          <span v-if="evidencePayloadEstimate && evidencePayloadEstimate.approxTokens > 0" class="token_info">· pribl.
            {{ evidencePayloadEstimate.approxTokens.toLocaleString() }} tokenov, {{
              evidencePayloadEstimate.includedDocs ===
                evidencePayloadEstimate.totalFilteredDocs ? 'vsi dokumenti' : 'prvih ' +
                evidencePayloadEstimate.includedDocs
                + ' od ' + evidencePayloadEstimate.totalFilteredDocs + ' dokumentov' }} (pošlje se LLM ob
            analizi)</span>
          <span v-if="estimatedAnalyzeCost" class="token_info">· pribl. cena analize ({{ estimatedAnalyzeCost.model }}):
            ${{ estimatedAnalyzeCost.total.toFixed(4) }} (vhod ${{ estimatedAnalyzeCost.inputCost.toFixed(4) }}, odgovor
            ~${{ estimatedAnalyzeCost.outputCost.toFixed(4) }})</span>
        </div>
        <div class="expand_actions">
          <button type="button" class="button gray" @click="expandAll">Razširi vse</button>
          <button type="button" class="button gray" @click="collapseAll">Skrči vse</button>
        </div>
      </div>

      <div v-if="resultData && !loading && resultData['@odata.count'] === 0" class="warning">
        <p>Ni najdenih dokumentov za izbrane kriterije. Poskusi z manj filtri, drugačnim iskalnim nizom ali večjim številom
          rezultatov.</p>
      </div>

      <div v-if="tableRows.length" class="collapse_section">
        <h4 class="collapse_toggle" @click="toggleSection('table')">
          <span class="collapse_icon">{{ expandedSections.table ? '▼' : '▶' }}</span>
          Tabela rezultatov (top {{ tableRows.length }} od {{ resultData['@odata.count'] }} rezultatov)
        </h4>

        <div v-show="expandedSections.table" class="result_table_export_actions">
          <div class="file_exports" @mouseleave="hideExportMenu">
            <div class="export_dropdown" @mouseenter="showExportMenu = true">
              <div class="export_trigger" @click="toggleExportMenu">
                <font-awesome-icon :icon="['fas', 'file-arrow-down']" />
              </div>

              <div class="export_menu" v-show="showExportMenu">
                <div class="export_menu_item" @click="exportXls" title="Izvoz Excel rezultatov iskanja">
                  <font-awesome-icon :icon="['far', 'file-excel']" />
                  <span>Izvoz Excel rezultatov iskanja</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-show="expandedSections.table" class="result_table_wrapper">
          <table class="result_table">
            <colgroup>
              <col v-for="col in tableColumns" :key="col" :style="{ width: columnWidths[col] + 'px' }" />
            </colgroup>
            <thead>
              <tr>
                <th v-for="col in tableColumns" :key="col" :title="col"><span class="th_label">{{ col }}</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in tableRows" :key="idx">
                <td v-for="col in tableColumns" :key="col" :title="formatCell(row[col])"
                  v-html="wrapCellHtml(formatCell(row[col]))"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="facetEntries.length" class="collapse_section">
        <h4 class="collapse_toggle" @click="toggleSection('facets')">
          <span class="collapse_icon">{{ expandedSections.facets ? '▼' : '▶' }}</span>
          Tabele sumarnih podatkov
        </h4>
        <div v-show="expandedSections.facets" class="facets_section">
          <div v-for="facet in facetEntries" :key="facet.name" class="facet_block">
            <h5>{{ facet.name }}</h5>
            <table class="facets_table">
              <thead>
                <tr>
                  <th>Vrednost</th>
                  <th>Število</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(bucket, i) in facet.buckets" :key="i">
                  <td>{{ formatFacetValue(facet.name, bucket.value) }}</td>
                  <td>{{ bucket.count }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="collapse_section">
        <h4 class="collapse_toggle" @click="toggleSection('rawJson')">
          <span class="collapse_icon">{{ expandedSections.rawJson ? '▼' : '▶' }}</span>
          Raw JSON (JSON rezultati iskanja)
        </h4>
        <div v-show="expandedSections.rawJson">
          <pre class="result_json">{{ resultJson }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    filterOptions: { type: Object, default: null },
    analyzeModel: { type: String, default: 'gpt-5-mini' },
  },
  emits: ['export-xls'],
  data() {
    return {
      showExportMenu: false,
      expandedSections: {
        table: true,
        facets: true,
        rawJson: false
      }
    };
  },
  computed: {
    loading() {
      return this.$store.getters.evalvation_search_loading === true;
    },
    error() {
      return this.$store.getters.evalvation_search_error;
    },
    resultData() {
      return this.$store.getters.evalvation_search_data;
    },
    evidencePayloadEstimate() {
      const model = this.analyzeModel || 'gpt-5-mini';
      const MAX_TOTAL_CHARS = (model === 'gpt-5-mini' || model === 'gpt-5-nano') ? 750000 : 350000;
      const empty = { approxTokens: 0, includedDocs: 0, totalFilteredDocs: 0 };
      const data = this.resultData;
      if (!data || typeof data !== 'object') return empty;
      const hits = Array.isArray(data.value) ? data.value : [];
      if (!Array.isArray(hits) || hits.length === 0) return empty;
      const filtered = hits.filter((hit) => {
        if (!hit || typeof hit !== 'object') return false;
        if (hit.q_has_answer === false) return false;
        const content = hit.content != null
          ? (typeof hit.content === 'string' ? hit.content : JSON.stringify(hit.content))
          : '';
        if (typeof content !== 'string' || content.trim() === '') return false;
        return true;
      });
      let evidence = [];
      for (const hit of filtered) {
        const candidate = [...evidence, hit];
        if (JSON.stringify(candidate).length > MAX_TOTAL_CHARS) break;
        evidence = candidate;
      }
      const payloadLen = JSON.stringify(evidence).length;
      return {
        approxTokens: Math.ceil(payloadLen / 4),
        includedDocs: evidence.length,
        totalFilteredDocs: filtered.length
      };
    },
    estimatedAnalyzeCost() {
      const model = this.analyzeModel || 'gpt-5-mini';
      const MODEL_PRICES = {
        'gpt-5-nano': { input: 0.05, output: 0.4 },
        'gpt-5-mini': { input: 0.25, output: 2.0 },
        'gpt-4o-mini': { input: 0.15, output: 0.6 }
      };
      const ESTIMATED_OUTPUT_TOKENS = 800;
      const ESTIMATED_INPUT_OVERHEAD = 4000;
      const prices = MODEL_PRICES[model];
      if (!prices) return null;
      const inputTokens = this.evidencePayloadEstimate.approxTokens + ESTIMATED_INPUT_OVERHEAD;
      if (inputTokens <= ESTIMATED_INPUT_OVERHEAD) return null;
      const outputTokens = ESTIMATED_OUTPUT_TOKENS;
      const inputCost = (inputTokens / 1e6) * prices.input;
      const outputCost = (outputTokens / 1e6) * prices.output;
      return {
        model,
        inputTokens,
        outputTokens,
        inputCost,
        outputCost,
        total: inputCost + outputCost
      };
    },
    resultJson() {
      return this.resultData ? JSON.stringify(this.resultData, null, 2) : null;
    },
    tableRows() {
      if (!this.resultData) return [];
      const d = this.resultData;
      const rows = Array.isArray(d.value) ? d.value : (Array.isArray(d) ? d : []);
      return rows;
    },
    tableColumns() {
      const rows = this.tableRows;
      const keys = new Set();
      rows.forEach(row => {
        if (row && typeof row === 'object' && !Array.isArray(row)) {
          Object.keys(row).forEach(k => keys.add(k));
        }
      });
      return [...keys].sort((a, b) => {
        const aAt = a.startsWith('@') ? 0 : 1;
        const bAt = b.startsWith('@') ? 0 : 1;
        if (aAt !== bAt) return aAt - bAt;
        return a.localeCompare(b);
      });
    },
    columnWidths() {
      const rows = this.tableRows;
      const cols = this.tableColumns;
      const widths = {};
      cols.forEach(col => {
        let maxLen = 0;
        rows.forEach(row => {
          const s = this.formatCell(row[col]);
          if (s.length > maxLen) maxLen = s.length;
        });
        const px = Math.min(200, Math.max(40, Math.ceil(maxLen * 6)));
        widths[col] = px;
      });
      return widths;
    },
    facetEntries() {
      if (!this.resultData || typeof this.resultData['@search.facets'] !== 'object') return [];
      const facets = this.resultData['@search.facets'];
      return Object.keys(facets).map(name => ({
        name,
        buckets: Array.isArray(facets[name]) ? facets[name] : []
      })).filter(f => f.buckets.length > 0);
    },
  },
  methods: {
    toggleSection(key) {
      this.expandedSections[key] = !this.expandedSections[key];
    },
    expandAll() {
      this.expandedSections.table = true;
      this.expandedSections.facets = true;
      this.expandedSections.rawJson = true;
    },
    collapseAll() {
      this.expandedSections.table = false;
      this.expandedSections.facets = false;
      this.expandedSections.rawJson = false;
    },
    toggleExportMenu() {
      this.showExportMenu = !this.showExportMenu;
    },
    hideExportMenu() {
      this.showExportMenu = false;
    },
    formatCell(val) {
      if (val === null || val === undefined) return '';
      if (val === true) return '1';
      if (val === false) return '0';
      if (typeof val === 'object') return JSON.stringify(val);
      return String(val);
    },
    wrapCellHtml(text) {
      const escaped = String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      return escaped.replace(/([\s\-/_])/g, '$1<wbr>');
    },
    formatFacetValue(facetName, value) {
      const fo = this.filterOptions;
      if (!fo) return this.formatCell(value);

      if (facetName === 'ul_program_id' && Array.isArray(fo.ul_program_id)) {
        const numVal = Number(value);
        const item = fo.ul_program_id.find(o => Number(o.value) === numVal);
        if (item && item.name != null) return item.name;
      }
      if (facetName === 'sep_page_internal_id' && Array.isArray(fo.sep_page_internal_id)) {
        const item = fo.sep_page_internal_id.find(o => String(o.value) === String(value));
        if (item && item.label != null) return `(${value}) ${item.label}`;
      }
      if (facetName === 'q_internal_id' && Array.isArray(fo.q_internal_id)) {
        const item = fo.q_internal_id.find(o => String(o.value) === String(value));
        if (item && item.label != null) return `(${value}) ${item.label}`;
      }
      return this.formatCell(value);
    },
    getTableDataForExport() {
      const cols = Array.isArray(this.tableColumns) ? this.tableColumns : [];
      const rows = Array.isArray(this.tableRows) ? this.tableRows : [];
      const data = [cols];
      rows.forEach((row) => {
        data.push(cols.map((col) => this.formatCell(row ? row[col] : null)));
      });
      return data;
    },
    exportXls() {
      // Prefer using store action directly (smart panel), but keep emit for compatibility
      const data = this.getTableDataForExport();
      if (this.$store && this.$store.dispatch) {
        this.$store.dispatch('exportEvalvationAiPlainData', {
          format: 'xls',
          filename: 'rezultati_iskanja_sep_ai',
          data
        });
      } else {
        this.$emit('export-xls', data);
      }
    }
  }
};
</script>

